import Logger, { LogLevels } from "./Logger";
const TAG = 'FrameMessages';
export class FrameMessages {
    constructor() {
        this.frames = {};
        this.whiteListedDomains = [];
        this.isListenerAttached = false;
        this.receiveMessage = (event) => {
            if (!this.isValidMessageEvent(event)) {
                return;
            }
            const sourceWindow = event.source;
            const frameTag = this.findFrameTag(sourceWindow);
            if (frameTag === null) {
                return;
            }
            this.processMessage(frameTag, event.data);
        };
    }
    startListening() {
        if (this.isListenerAttached) {
            Logger.error(TAG, `Attempted to re-attach message listener to window...`);
            return;
        }
        Logger.debug(TAG, `Attaching message listener to window...`);
        window.addEventListener('message', this.receiveMessage, false);
        this.isListenerAttached = true;
    }
    stopListening() {
        if (!this.isListenerAttached) {
            Logger.debug(TAG, `Attempted to detach message listener from window when listener is not attached...`);
            return;
        }
        Logger.debug(TAG, `Removing message listener from window...`);
        window.removeEventListener('message', this.receiveMessage, false);
        this.isListenerAttached = false;
    }
    addDomainToWhiteList(domainName) {
        if (!this.whiteListedDomains.includes(domainName)) {
            this.whiteListedDomains.push(domainName);
        }
    }
    registerFrame(frameTag, frame) {
        if (!this.isValidFrameTag(frameTag, LogLevels.ERROR)) {
            return false;
        }
        if (!frame) {
            Logger.error(TAG, `Attempting to register invalid frame for ${frameTag}...`);
            return false;
        }
        Logger.debug(TAG, `Frame registered for ${frameTag}...`);
        this.frames[frameTag] = frame;
        return true;
    }
    removeRegisteredFrame(frameTag) {
        if (!this.isValidFrameTag(frameTag, LogLevels.DEBUG)) {
            return false;
        }
        Logger.debug(TAG, `Removing registered frame ${frameTag}...`);
        delete this.frames[frameTag];
        return true;
    }
    isValidFrameTag(frameTag, logLevel) {
        return this.isString(frameTag, 'Invalid frame tag, string expected...', logLevel);
    }
    isValidMessageEvent(event) {
        // Check event origin
        if (event.origin !== window.origin) {
            // Check whether origin in from a trusted third party site.
            if (!this.whiteListedDomains.includes(event.origin)) {
                Logger.error(TAG, `Received message from foreign origin: '${event.origin}', expected '${window.origin}`);
                return false;
            }
        }
        // Detect source frame
        const sourceWindow = event.source;
        if (!this.isFrameRegistered(sourceWindow) && (event.data && event.data.type !== 'show_notification')) {
            Logger.debug(TAG, `Ignored message from not registered frame:  '${sourceWindow.location}': (${JSON.stringify(event.data)})`);
            return false;
        }
        const message = event.data;
        return this.isValidMessage(message);
    }
    isString(variable, logMessage, logLevel) {
        const valid = typeof (variable) === 'string';
        if (!valid) {
            Logger.log(TAG, logMessage, logLevel);
        }
        return valid;
    }
    isFrameRegistered(frame) {
        return this.findFrameTag(frame) !== null;
    }
    findFrameTag(frame) {
        for (const frameTag in this.frames) {
            if (!this.frames.hasOwnProperty(frameTag)) {
                continue;
            }
            if (frame === this.frames[frameTag]) {
                return frameTag;
            }
        }
        return null;
    }
}
class AppLovinFrameMessages extends FrameMessages {
    constructor() {
        super(...arguments);
        this.messageProcessors = {};
    }
    processMessage(frameTag, message) {
        if (!this.messageProcessors[message.type]) {
            return;
        }
        Logger.info(TAG, `Processing ${message.type} message from frame:  '${frameTag}'...`);
        this.messageProcessors[message.type].forEach(processor => processor(message));
    }
    registerMessageProcessor(messageType, messageProcessor) {
        if (!this.isValidMessageTypeAndMessageProcessor(messageType, messageProcessor, LogLevels.ERROR)) {
            return false;
        }
        Logger.debug(TAG, `Message processor registered for type: ${messageType}...`);
        if (typeof (this.messageProcessors[messageType]) === 'undefined') {
            this.messageProcessors[messageType] = [];
        }
        this.messageProcessors[messageType].push(messageProcessor);
        return true;
    }
    removeRegisteredMessageProcessor(messageType, messageProcessor) {
        if (!this.isValidMessageTypeAndMessageProcessor(messageType, messageProcessor, LogLevels.DEBUG)) {
            return false;
        }
        Logger.debug(TAG, `Removing message processor for: ${messageType}...`);
        this.messageProcessors[messageType].filter(currentProcessor => currentProcessor !== messageProcessor);
        return true;
    }
    isValidMessageTypeAndMessageProcessor(messageType, messageProcessor, logLevel) {
        return this.isValidMessageType(messageType, logLevel)
            && this.isValidMessageProcessor(messageProcessor, logLevel);
    }
    isValidMessageProcessor(messageProcessor, logLevel) {
        const valid = typeof (messageProcessor) === 'function';
        if (!valid) {
            Logger.log(TAG, 'Invalid message processor, function expected...', logLevel);
        }
        return valid;
    }
    isValidMessage(message) {
        if (this.isValidateMessageStructure(message)) {
            if (message.hasOwnProperty('source')) // React dev tools messages, we can ignore.
             {
                return false;
            }
            else {
                Logger.error(TAG, `Invalid message: 'type' field is missing...`);
                return false;
            }
        }
        return this.isValidMessageType(message.type, LogLevels.ERROR);
    }
    isValidateMessageStructure(message) {
        return !Object.prototype.hasOwnProperty.call(message, 'type');
    }
    isValidMessageType(messageType, logLevel) {
        return this.isString(messageType, 'Message type is not a string', logLevel);
    }
}
// create single app instance
const FrameMessagesAPI = new AppLovinFrameMessages();
export default FrameMessagesAPI;
//# sourceMappingURL=FrameMessagesAPI.js.map